import type { SkillDto, ToolDto, UpdateSkillRequest } from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, FileText, Lock } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/error-alert';
import { NoMatches, useListSearch } from '@/components/list-search';
import { PromptEditor } from '@/components/prompt-editor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1).replace(/\.0$/, '')} KB`;
  return `${(size / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
}

function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

function FieldError({ error, path }: { error: unknown; path: string }) {
  const message = error instanceof ApiError ? error.fieldError(path) : undefined;
  if (!message) return null;
  return <p className="text-destructive text-xs">{message}</p>;
}

/** One reference file: path and size, with its content loaded when expanded. */
function SkillFileRow({
  skill,
  file,
  tenantId,
}: {
  skill: SkillDto;
  file: { path: string; size: number };
  tenantId: string;
}) {
  const [open, setOpen] = useState(false);
  const content = useQuery({
    queryKey: ['skill-file', tenantId, skill.id, file.path],
    queryFn: () =>
      api<{ path: string; content: string }>(
        `/skills/${skill.id}/files?path=${encodeURIComponent(file.path)}`,
      ),
    enabled: open,
  });
  const previewId = `skill-file-${skill.id}-${file.path}`;
  return (
    <li className="border-b last:border-b-0">
      <button
        type="button"
        className="hover:bg-muted/50 flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
        aria-expanded={open}
        aria-controls={open ? previewId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronRight
          className={cn(
            'text-muted-foreground size-3.5 shrink-0 transition-transform',
            open && 'rotate-90',
          )}
          aria-hidden
        />
        <FileText className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate font-mono">{file.path}</span>
        <span className="text-muted-foreground shrink-0 text-xs">{formatBytes(file.size)}</span>
      </button>
      {open && (
        <div id={previewId} className="px-3 pb-3">
          <ErrorAlert error={content.error} />
          {!content.error && (
            <pre className="bg-muted max-h-64 overflow-auto rounded-md p-3 font-mono text-xs whitespace-pre-wrap">
              {content.data?.content ?? 'Loading…'}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}

function SkillForm({
  skill,
  tools,
  tenantId,
  readOnly,
  readOnlyReason,
  onDone,
}: {
  skill: SkillDto;
  tools: ToolDto[];
  tenantId: string;
  readOnly: boolean;
  readOnlyReason: string | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [description, setDescription] = useState(skill.description);
  const [instructions, setInstructions] = useState(skill.instructions);
  const [toolIds, setToolIds] = useState<string[]>(skill.toolIds);
  const toolSearch = useListSearch(tools, (t) => [t.name, t.description]);

  const changes: UpdateSkillRequest = {};
  if (description !== skill.description) changes.description = description;
  if (instructions !== skill.instructions) changes.instructions = instructions;
  if (!sameIds(toolIds, skill.toolIds)) changes.toolIds = toolIds;
  const dirty = Object.keys(changes).length > 0;

  const save = useMutation({
    mutationFn: (body: UpdateSkillRequest) =>
      api<SkillDto>(`/skills/${skill.id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      toast.success('Skill saved');
      void queryClient.invalidateQueries({ queryKey: ['skills', tenantId] });
      onDone();
    },
  });

  const unknownToolIds = toolIds.filter((id) => !tools.some((t) => t.id === id));

  return (
    <form
      className="flex min-h-0 flex-1 flex-col gap-4"
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (!readOnly && dirty) save.mutate(changes);
      }}
    >
      <div className="-mx-1 grid min-h-0 flex-1 gap-5 overflow-y-auto px-1 pb-1">
        {readOnlyReason && (
          <p className="bg-muted text-muted-foreground flex items-start gap-2 rounded-md px-3 py-2 text-xs">
            <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {readOnlyReason}
          </p>
        )}
        <ErrorAlert error={save.error} />
        <div className="grid gap-4 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
          <div className="grid content-start gap-1.5">
            <Label htmlFor="edit-skill-name">Name</Label>
            <Input id="edit-skill-name" className="font-mono" value={skill.name} readOnly />
            <p className="text-muted-foreground text-xs">Names can’t be changed.</p>
          </div>
          <div className="grid content-start gap-1.5">
            <Label htmlFor="edit-skill-description">Description</Label>
            <Input
              id="edit-skill-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={readOnly}
              placeholder="What the skill is for"
            />
            <FieldError error={save.error} path="description" />
          </div>
        </div>

        <div className="grid gap-1.5">
          <PromptEditor
            id="edit-skill-instructions"
            label="Instructions"
            heading={<Label htmlFor="edit-skill-instructions">Instructions</Label>}
            value={instructions}
            onChange={setInstructions}
            disabled={readOnly}
            placeholder="How agents should use this skill…"
            className="h-[min(45vh,24rem)] min-h-48"
          />
          <FieldError error={save.error} path="instructions" />
        </div>

        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium">Bundled tools</legend>
          <p className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
            <span>Agents with this skill get these tools as well.</span>
            <span className="shrink-0">{toolIds.length} selected</span>
          </p>
          {tools.length === 0 ? (
            <p className="text-muted-foreground text-xs">This tenant has no tools yet.</p>
          ) : (
            <>
              <Input
                type="search"
                aria-label="Search tools"
                placeholder="Search tools"
                value={toolSearch.query}
                onChange={(e) => toolSearch.setQuery(e.target.value)}
              />
              <ul className="max-h-56 overflow-y-auto rounded-md border" aria-label="Tools">
                {toolSearch.filtered.length === 0 ? (
                  <li>
                    <NoMatches query={toolSearch.query} />
                  </li>
                ) : (
                  toolSearch.filtered.map((t) => (
                    <li key={t.id} className="border-b last:border-b-0">
                      <label
                        className={cn(
                          'flex items-start gap-2.5 px-3 py-2 text-sm',
                          !readOnly && 'hover:bg-muted/50 cursor-pointer',
                        )}
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={toolIds.includes(t.id)}
                          disabled={readOnly}
                          onChange={(e) =>
                            setToolIds((ids) =>
                              e.target.checked ? [...ids, t.id] : ids.filter((i) => i !== t.id),
                            )
                          }
                        />
                        <span className="grid min-w-0 flex-1">
                          <span className="font-mono">{t.name}</span>
                          {t.description && (
                            <span className="text-muted-foreground truncate text-xs">
                              {t.description}
                            </span>
                          )}
                        </span>
                        <Badge variant="outline" className="shrink-0">
                          {t.kind}
                        </Badge>
                      </label>
                    </li>
                  ))
                )}
              </ul>
            </>
          )}
          {unknownToolIds.length > 0 && (
            <p className="text-muted-foreground text-xs">
              Also bundles {unknownToolIds.length} tool{unknownToolIds.length === 1 ? '' : 's'} that
              could not be loaded.
            </p>
          )}
          <FieldError error={save.error} path="toolIds" />
        </fieldset>

        {skill.files.length > 0 && (
          <section className="grid gap-2" aria-labelledby="edit-skill-files">
            <div>
              <h3 id="edit-skill-files" className="text-sm font-medium">
                Reference files
              </h3>
              <p className="text-muted-foreground text-xs">
                Imported with the skills folder. Agents read them with{' '}
                <code className="font-mono">read_skill_file</code>; they can’t be edited here.
              </p>
            </div>
            <ul className="rounded-md border" aria-label={`Files of ${skill.name}`}>
              {skill.files.map((f) => (
                <SkillFileRow key={f.path} skill={skill} file={f} tenantId={tenantId} />
              ))}
            </ul>
          </section>
        )}
      </div>

      <DialogFooter>
        {readOnly ? (
          <Button type="button" variant="outline" onClick={onDone}>
            Close
          </Button>
        ) : (
          <>
            <Button type="button" variant="ghost" onClick={onDone}>
              Cancel
            </Button>
            <Button type="submit" disabled={!dirty || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        )}
      </DialogFooter>
    </form>
  );
}

/**
 * Edit a skill's description, instructions and bundled tools. Names are immutable; skills a
 * plugin installed (or any skill, for viewers) open read-only.
 */
export function EditSkillDialog({
  skill,
  open,
  onOpenChange,
  tools,
  tenantId,
  canEdit,
}: {
  skill: SkillDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tools: ToolDto[];
  tenantId: string;
  canEdit: boolean;
}) {
  const readOnlyReason = !skill
    ? null
    : skill.pluginId
      ? 'This skill is managed by the plugin that installed it. Update or uninstall the plugin to change it.'
      : !canEdit
        ? 'You need the admin role to change skills.'
        : null;
  const readOnly = readOnlyReason !== null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] flex-col sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{readOnly ? 'Skill' : 'Edit skill'}</DialogTitle>
          <DialogDescription>
            {skill ? (
              <>
                <span className="font-mono">{skill.name}</span> — agents with this skill get its
                instructions (under “## Skill: {skill.name}”) and its tools.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        {skill && (
          <SkillForm
            // Remount per skill and per opening so the form starts from the saved values.
            key={`${skill.id}:${skill.updatedAt}:${String(open)}`}
            skill={skill}
            tools={tools}
            tenantId={tenantId}
            readOnly={readOnly}
            readOnlyReason={readOnlyReason}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
